import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { PollResponseFilters, TabulationPermission, User } from '../../../types';
import React, { useEffect, useState } from 'react';
import {
  fetchBarangaysByMunicipality,
  fetchMunicipalitiesByDistrict,
  fetchPrecinctsByBarangay
} from '../../../utils/locationUtils';

import { fetchAllCandidates } from '../../../utils/candidates';
import { fetchPollUsers } from '../../../utils/userUtils';
import useAuth from '../../../hooks/useAuth';

interface PollResponseFormProps {
  onGenerateReport: (filters: PollResponseFilters) => void;
}

const PollResponseForm: React.FC<PollResponseFormProps> = ({ onGenerateReport }) => {
  const { user } = useAuth();

  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [barangays, setBarangays] = useState<string[]>([]);
  const [precincts, setPrecincts] = useState<string[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [positions, setPositions] = useState<string[]>([]);

  const [filters, setFilters] = useState<PollResponseFilters>({
    region: 'REGION XIII',
    province: 'SURIGAO DEL NORTE',
    district: 'DISTRICT 1',
    municipality: '',
    barangay: '',
    precinct: '',
    position: '',
  });

  useEffect(() => {
    const loadInitialData = async (user: User) => {
      try {
        const loadedCandidates = await fetchAllCandidates();
        setUsers(await fetchPollUsers());
        
        // Extract unique positions from candidates
        const uniquePositions = Array.from(new Set(loadedCandidates.map(candidate => candidate.position)));
        setPositions(uniquePositions);

        const districtMunicipalities = await fetchMunicipalitiesByDistrict(
          filters.region, 
          filters.province, 
          filters.district
        );

        // Filter municipalities based on user's allowedTabulations if set
        const filteredMunicipalities = user.allowedTabulations 
          ? districtMunicipalities.filter(municipality => 
              user.allowedTabulations?.some(
                (permission: TabulationPermission) => 
                  permission.type === 'municipal' && permission.id === municipality
              )
            )
          : districtMunicipalities;

        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    if (user) {
      loadInitialData(user);
    }
  }, [user]);

  const handleMunicipalityChange = async (municipality: string) => {
    try {
      const newBarangays = await fetchBarangaysByMunicipality(
        filters.region, 
        filters.province, 
        filters.district, 
        municipality
      );
      setBarangays(newBarangays);
      setFilters(prev => ({
        ...prev,
        municipality,
        barangay: '',
        precinct: ''
      }));
      // Reset subsequent dropdowns
      setPrecincts([]);
    } catch (error) {
      console.error('Error fetching barangays:', error);
    }
  };

  const handleBarangayChange = async (barangay: string) => {
    try {
      const newPrecincts = await fetchPrecinctsByBarangay(
        filters.region, 
        filters.province, 
        filters.district, 
        filters.municipality, 
        barangay
      );
      setPrecincts(newPrecincts);
      setFilters(prev => ({
        ...prev,
        barangay,
        precinct: ''
      }));
    } catch (error) {
      console.error('Error fetching precincts:', error);
    }
  };

  const handleSubmit = () => {
    // Validate mandatory fields
    if (!filters.region || !filters.province || !filters.district || 
        !filters.municipality || !filters.barangay || 
        !filters.position) {
      alert('Please fill in all required fields');
      return;
    }

    onGenerateReport(filters);
  };

  return (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      {/* Municipality Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Municipality *</InputLabel>
          <Select
            value={filters.municipality}
            label="Municipality *"
            onChange={(e) => handleMunicipalityChange(e.target.value)}
            disabled={!filters.district}
            required
          >
            {municipalities.map(municipality => (
              <MenuItem key={municipality} value={municipality}>
                {municipality}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Barangay Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Barangay *</InputLabel>
          <Select
            value={filters.barangay}
            label="Barangay *"
            onChange={(e) => handleBarangayChange(e.target.value)}
            disabled={!filters.municipality}
            required
          >
            {barangays.map(barangay => (
              <MenuItem key={barangay} value={barangay}>
                {barangay}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Optional Precinct Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Precinct (Optional)</InputLabel>
          <Select
            value={filters.precinct || ''}
            label="Precinct (Optional)"
            onChange={(e) => setFilters(prev => ({
              ...prev,
              precinct: e.target.value
            }))}
            disabled={!filters.barangay}
          >
            <MenuItem value="">All Precincts</MenuItem>
            {precincts.map(precinct => (
              <MenuItem key={precinct} value={precinct}>
                {precinct}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Required Position Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Position *</InputLabel>
          <Select
            value={filters.position || ''}
            label="Position *"
            onChange={(e) => setFilters(prev => ({
              ...prev,
              position: e.target.value
            }))}
            required
          >
            {positions.map(position => (
              <MenuItem key={position} value={position}>
                {position}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Optional Poller Dropdown */}
      <Grid item xs={12} md={4}>
        <FormControl fullWidth>
          <InputLabel>Poller (Optional)</InputLabel>
          <Select
            value={filters.poller ?? ''}
            label="Poller (Optional)"
            onChange={(e) => setFilters(prev => ({
              ...prev,
              poller: e.target.value || undefined,
              pollerMatchType: e.target.value ? prev.pollerMatchType || 'match' : undefined
            }))}
          >
            <MenuItem value="">All Pollers</MenuItem>
            <MenuItem value="Dapa Import">Dapa Import</MenuItem>
            <MenuItem value="Socorro Import">Socorro Import</MenuItem>
            {users.map(user => (
              <MenuItem key={user.uid} value={user.display_name}>
                {user.display_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Poller Match Type Dropdown (only show when a poller is selected) */}
      {filters.poller && (
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Poller Match Type</InputLabel>
            <Select
              value={filters.pollerMatchType || 'match'}
              label="Poller Match Type"
              onChange={(e) => setFilters(prev => ({
                ...prev,
                pollerMatchType: e.target.value as 'match' | 'exclude'
              }))}
            >
              <MenuItem value="match">Match Poller</MenuItem>
              <MenuItem value="exclude">Exclude Poller</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Submit Button */}
      <Grid item xs={12}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={!filters.region || !filters.province || 
                    !filters.district || !filters.municipality || 
                    !filters.barangay || !filters.position}
        >
          Generate Report
        </Button>
      </Grid>
    </Grid>
  );
};

export default PollResponseForm;
